import React, { useState, useEffect } from 'react';
import {
  Typography,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Message,
  Card,
  Space,
  Popconfirm
} from '@arco-design/web-react';
import {
  IconPlus,
  IconDelete,
  IconRefresh,
  IconLock
} from '@arco-design/web-react/icon';
import { useAppDispatch, useAppSelector } from '../store';
import { fetchAllUsers, createUser, deleteUser, clearError, changePassword } from '../store/slices/userSlice';
import { CreateUserRequest } from '../types';

const { Title } = Typography;
const FormItem = Form.Item;
const Option = Select.Option;

const UserManagementPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { users, loading, error } = useAppSelector((state) => state.users);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<{id: string, username: string} | null>(null);
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();

  // 初始加载用户列表
  useEffect(() => {
    dispatch(fetchAllUsers());
  }, [dispatch]);

  // 如果有错误，显示错误信息
  useEffect(() => {
    if (error) {
      Message.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // 表格列定义
  const columns = [
    {
      title: '用户名',
      dataIndex: 'username',
    },
    {
      title: '角色',
      dataIndex: 'role',
      render: (role: string) => (
        <span>{role === 'ADMIN' ? '管理员' : '普通用户'}</span>
      )
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      render: (date: string) => {
        try {
          // 尝试解析日期，如果是ISO格式则正确显示
          if (date && date.trim()) {
            return new Date(date).toLocaleString('zh-CN');
          }
          return '未知';
        } catch (e) {
          console.error('日期解析错误:', date, e);
          return '无效日期';
        }
      }
    },
    {
      title: '操作',
      render: (_: any, record: any) => (
        <Space>
          <Button 
            type="text" 
            icon={<IconLock />}
            onClick={() => showPasswordModal(record)}
          >
            修改密码
          </Button>
          <Popconfirm
            title="确定要删除此用户吗？"
            okText="确定"
            cancelText="取消"
            position="br"
            onOk={() => handleDeleteUser(record.id)}
          >
            <Button type="text" status="danger" icon={<IconDelete />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  // 刷新用户列表
  const handleRefresh = () => {
    dispatch(fetchAllUsers());
  };

  // 显示创建用户对话框
  const showCreateModal = () => {
    form.resetFields();
    setCreateModalVisible(true);
  };

  // 关闭创建用户对话框
  const hideCreateModal = () => {
    setCreateModalVisible(false);
  };

  // 显示修改密码对话框
  const showPasswordModal = (user: any) => {
    setSelectedUser({id: user.id, username: user.username});
    passwordForm.resetFields();
    setPasswordModalVisible(true);
  };

  // 关闭修改密码对话框
  const hidePasswordModal = () => {
    setPasswordModalVisible(false);
    setSelectedUser(null);
  };

  // 创建用户
  const handleCreateUser = async () => {
    try {
      const values = await form.validate();
      
      // 删除确认密码字段，因为后端不需要这个字段
      const { confirmPassword, ...userData } = values;
      
      // 确保role字段是正确的枚举值
      const createUserData: CreateUserRequest = {
        username: userData.username,
        password: userData.password,
        role: userData.role as 'ADMIN' | 'USER'
      };
      
      await dispatch(createUser(createUserData)).unwrap();
      Message.success('创建用户成功');
      hideCreateModal();
    } catch (error: any) {
      // 更详细的错误处理
      console.error('创建用户失败:', error);
      if (error.response?.data?.message) {
        Message.error(`创建用户失败: ${error.response.data.message}`);
      } else if (typeof error === 'string') {
        Message.error(`创建用户失败: ${error}`);
      } else {
        Message.error('创建用户失败，请检查表单并重试');
      }
    }
  };

  // 修改密码
  const handleChangePassword = async () => {
    if (!selectedUser) return;
    
    try {
      const values = await passwordForm.validate();
      
      await dispatch(changePassword({
        userId: selectedUser.id,
        newPassword: values.newPassword
      })).unwrap();
      
      Message.success('密码修改成功');
      hidePasswordModal();
    } catch (error: any) {
      console.error('修改密码失败:', error);
      if (typeof error === 'string') {
        Message.error(`修改密码失败: ${error}`);
      } else {
        Message.error('修改密码失败，请检查表单并重试');
      }
    }
  };

  // 删除用户
  const handleDeleteUser = async (userId: string) => {
    try {
      await dispatch(deleteUser(userId)).unwrap();
      Message.success('删除用户成功');
    } catch (error) {
      console.error('删除用户失败:', error);
    }
  };

  return (
    <div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title heading={4} style={{ margin: 0 }}>用户管理</Title>
          <Space>
            <Button 
              type="primary" 
              icon={<IconPlus />} 
              onClick={showCreateModal}
            >
              创建用户
            </Button>
            <Button 
              icon={<IconRefresh />} 
              onClick={handleRefresh}
            >
              刷新
            </Button>
          </Space>
        </div>

        <Table
          loading={loading}
          columns={columns}
          data={users}
          rowKey="id"
          border={false}
          pagination={{
            showTotal: true,
            sizeCanChange: true,
            pageSizeChangeResetCurrent: false,
          }}
        />
      </Card>

      {/* 创建用户对话框 */}
      <Modal
        title="创建新用户"
        visible={createModalVisible}
        onOk={handleCreateUser}
        onCancel={hideCreateModal}
        autoFocus={false}
        focusLock={true}
        maskClosable={false}
      >
        <Form 
          form={form}
          layout="vertical"
          autoComplete="off"
        >
          <FormItem
            label="用户名"
            field="username"
            rules={[
              { required: true, message: '请输入用户名' },
              { minLength: 3, message: '用户名长度不能少于3个字符' },
              { maxLength: 20, message: '用户名长度不能超过20个字符' }
            ]}
          >
            <Input placeholder="请输入用户名" />
          </FormItem>
          <FormItem
            label="密码"
            field="password"
            rules={[
              { required: true, message: '请输入密码' },
              { minLength: 6, message: '密码长度不能少于6个字符' }
            ]}
          >
            <Input.Password placeholder="请输入密码" />
          </FormItem>
          <FormItem
            label="确认密码"
            field="confirmPassword"
            dependencies={['password']}
            rules={[
              { required: true, message: '请确认密码' },
              {
                validator: (value, callback) => {
                  if (value !== form.getFieldValue('password')) {
                    callback('两次输入的密码不一致');
                  }
                }
              }
            ]}
          >
            <Input.Password placeholder="请再次输入密码" />
          </FormItem>
          <FormItem
            label="角色"
            field="role"
            rules={[{ required: true, message: '请选择用户角色' }]}
            initialValue="USER"
          >
            <Select placeholder="请选择用户角色">
              <Option value="USER">普通用户</Option>
              <Option value="ADMIN">管理员</Option>
            </Select>
          </FormItem>
        </Form>
      </Modal>

      {/* 修改密码对话框 */}
      <Modal
        title={`修改密码: ${selectedUser?.username || ''}`}
        visible={passwordModalVisible}
        onOk={handleChangePassword}
        onCancel={hidePasswordModal}
        autoFocus={false}
        focusLock={true}
        maskClosable={false}
      >
        <Form 
          form={passwordForm}
          layout="vertical"
          autoComplete="off"
        >
          <FormItem
            label="新密码"
            field="newPassword"
            rules={[
              { required: true, message: '请输入新密码' },
              { minLength: 6, message: '密码长度不能少于6个字符' }
            ]}
          >
            <Input.Password placeholder="请输入新密码" />
          </FormItem>
          <FormItem
            label="确认新密码"
            field="confirmPassword"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '请确认新密码' },
              {
                validator: (value, callback) => {
                  if (value !== passwordForm.getFieldValue('newPassword')) {
                    callback('两次输入的密码不一致');
                  }
                }
              }
            ]}
          >
            <Input.Password placeholder="请再次输入新密码" />
          </FormItem>
        </Form>
      </Modal>
    </div>
  );
};

export default UserManagementPage; 